/**
 * Format a price for display. Input is in minor units (cents).
 *
 * By default integers render with no decimals (e.g. `1000` → `$10`). Pass
 * `preserveCents: true` to keep fractional cents when present (e.g.
 * `990` → `$9.90`, while `1000` still renders as `$10`).
 */
export function formatPrice(
  price: number,
  currency: string,
  options?: { preserveCents?: boolean }
): string {
  const preserveCents = options?.preserveCents ?? false;
  const minimumFractionDigits = preserveCents && price % 100 !== 0 ? 2 : 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits,
    maximumFractionDigits: 2,
  }).format(price / 100);
}

/**
 * Format bytes to human readable format
 * @param bytes - Size in bytes
 * @returns Formatted string (e.g. "1.5 MB")
 */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Format a date for display
 * @param date Date to format
 * @returns Formatted date string in the format "Year/Month/Day"
 */
export function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}/${month}/${day}`;
}

/**
 * Format a date and time for display
 * @param date Date and time to format
 * @returns Formatted date string in the format "Year/Month/Day HH:MM:SS"
 */
export function formatDateTime(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hour = String(date.getHours()).padStart(2, '0');
  const minute = String(date.getMinutes()).padStart(2, '0');
  const second = String(date.getSeconds()).padStart(2, '0');
  return `${year}/${month}/${day} ${hour}:${minute}:${second}`;
}

/**
 * Format a number using compact notation (e.g. `1.2K`, `3.4M`). Used in
 * places where space is tight — stats badges, like / view counters, etc.
 *
 * @param value Numeric value to format
 * @returns Compact, locale-independent string (English numerals)
 */
export function formatCompactNumber(value: number): string {
  return new Intl.NumberFormat('en', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);
}

/**
 * Human-friendly elapsed time from a millisecond duration.
 *
 * @param ms Duration in milliseconds
 * @returns e.g. `320ms`, `12s`, `2m 5s`
 */
export function formatDuration(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return remainingSeconds > 0
    ? `${minutes}m ${remainingSeconds}s`
    : `${minutes}m`;
}

/**
 * Format a date or ISO date string as a human-readable long-form date
 * (e.g. `May 16, 2026`). Accepts either a `Date` instance or any input
 * the `Date` constructor understands so callers don't have to wrap
 * server-returned ISO strings.
 *
 * @param value Date instance, ISO string, or millis
 * @returns Long date string (English), or empty string if input is invalid
 */
export function formatLongDate(value: Date | string | number): string {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);
}

/**
 * Two-letter initials extracted from a display name. Falls back to the
 * first two visible characters when the name has no spaces (e.g. single-
 * token handles like `MrDasOnX` → `Mr`). The result is intentionally
 * mixed-case; callers should apply `uppercase` styling at the usage
 * site when needed.
 *
 * @param name Display name (any whitespace handling)
 * @returns Two-character initials, or `··` for an empty name
 */
export function getAuthorInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '··';
  if (parts.length === 1) return parts[0].slice(0, 2);
  return `${parts[0][0]}${parts[parts.length - 1][0]}`;
}
